import axios from "axios";
import React, { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useSelector } from "react-redux";
import CustomError from "src/app/libs/customError";

import UploadAttachments from "src/app/libs/uploadAttachments";
import FileField from "src/app/metafields/FileField/FileField";
import { addNewFilesFromFileList } from "src/app/metafields/FileField/utils";
import { MetaFileData } from "src/app/metafields/MultipleFileField/MultipleFileField";
import { RootState } from "src/redux/reducers";
import { PresignedUploadRequest, WsAssetParentTypeEnum } from "src/types";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast } from "src/utils/methods";
import {
  ALLOWED_TYPES_FOR_ATTACHMENT_UPLOAD,
  MAX_SIZE_SINGLE_ATTACHMENT,
} from "src/utils/uploadFunctionsUtils";

interface UploadFileFieldProps {
  selectedFiles: MetaFileData[];
  setSelectedFiles: Dispatch<SetStateAction<MetaFileData[]>>;
  isUploadingAttachment: boolean;
  setIsUploadingAttachment: Dispatch<SetStateAction<boolean>>;
  wsMetaFieldUuid: string;
  paymentUuid: string;
}

export default function UploadFileField({
  selectedFiles,
  setSelectedFiles,
  isUploadingAttachment,
  setIsUploadingAttachment,
  wsMetaFieldUuid,
  paymentUuid,
}: UploadFileFieldProps) {
  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const handleUploadFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files === null) {
      showErrorToast({
        id: "ws_no_file_selected",
        defaultMessage: "No file selected",
      });
      return;
    }

    if (selectedFiles.length > 0) {
      showErrorToast({
        id: "ws_you_cannot_upload_more_files",
        defaultMessage: "You cannot upload more files",
      });
      return;
    }

    setIsUploadingAttachment(true);

    try {
      const filesArray = Array.from(files);

      const uploadAttachments = new UploadAttachments({
        files: filesArray,
        allowedTypes: ALLOWED_TYPES_FOR_ATTACHMENT_UPLOAD,
        maxSize: MAX_SIZE_SINGLE_ATTACHMENT,
      });

      uploadAttachments.validate();

      const presignedRequestResponse = await axios.put<PresignedUploadRequest>(
        API_URLS.presignedRequestForNonExistingObjects,
        {
          wsObjectUuid: paymentUuid,
          wsWorkspaceUuid: activeWorkspaceUuid,
          wsMetaFieldUuid,
          wsAssetType: WsAssetParentTypeEnum.TYPE_WS_TASK_META_FIELD,
          fileNames: filesArray.map((file) => file.name),
        },
      );

      const { requestInputs } = presignedRequestResponse.data.content[0].token;

      const path = requestInputs.key.split("/");
      const realName = path[path.length - 1];

      const formattedFiles = addNewFilesFromFileList(files);
      const formattedFilesWithRealName = formattedFiles.map((file) => ({
        ...file,
        realName,
      }));

      setSelectedFiles(formattedFilesWithRealName);

      uploadAttachments.sendAttachmentsToS3(
        presignedRequestResponse.data.content,
      );
    } catch (error) {
      if (error instanceof CustomError) {
        showErrorToast({
          id: error.id,
          defaultMessage: error.defaultMessage,
        });
      } else {
        showErrorToast({
          id: "an_error_occurred_when_uploading_the_attachments",
          defaultMessage: "An error occurred when uploading the attachments.",
        });
      }
      console.error(error);
      setSelectedFiles([]);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleDeleteFile = async () => {
    setSelectedFiles([]);
  };

  return (
    <FileField
      filesData={selectedFiles}
      isLoading={isUploadingAttachment}
      handleDeleteFile={handleDeleteFile}
      handleUploadFile={handleUploadFiles}
      multiple={false}
    />
  );
}
